import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Form, FormFeedback, Input, Label, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "auth/useAuth";
import { translateAuthError, normalizeEmail } from "auth/cognitoClient";
import { getPendingConfirmation, setPendingConfirmation, clearPendingConfirmation } from "auth/pendingState";
import { takeSignupHandoff, clearSignupHandoff } from "auth/signupHandoff";
import { track, EVENTS } from "telemetry/track";
import AuthLayout from "./AuthLayout";

const RESEND_COOLDOWN_S = 60;

/**
 * Confirmation-code step. Designed to survive the tab being reloaded while
 * the user fetches the code from their e-mail app (the original mobile bug):
 * the e-mail comes from router state OR from localStorage, and confirming is
 * a stateless call — no server-side session to lose.
 */
const ConfirmAccount = () => {
  document.title = "Confirmar conta | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pending = useMemo(() => getPendingConfirmation(), []);
  const initialEmail = normalizeEmail(location.state?.email || pending?.email || "");
  const resumed = !location.state?.email && !!pending?.email; // came back after a reload

  const [email, setEmail] = useState(initialEmail);
  const [editingEmail, setEditingEmail] = useState(!initialEmail);
  const [submitError, setSubmitError] = useState(null);
  const [info, setInfo] = useState(
    location.state?.justSignedUp ? "Enviamos um código de 6 dígitos para o seu e-mail. Confira também a caixa de spam." : null
  );
  const [cooldown, setCooldown] = useState(location.state?.justSignedUp ? RESEND_COOLDOWN_S : 0);
  const [resending, setResending] = useState(false);

  // `resumed` = the page was opened from localStorage state, i.e. after a
  // reload/tab kill — the exact scenario the redesign set out to fix.
  useEffect(() => { track(EVENTS.CONFIRM_VIEWED, { resumed }); }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const form = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string().matches(/^\d{6}$/, "O código tem 6 números.").required("Digite o código recebido."),
    }),
    onSubmit: async ({ code }) => {
      setSubmitError(null);
      try {
        await auth.confirmSignUp(email, code);
        track(EVENTS.CONFIRMED, { resumed });
        // Same tab as the sign-up? Then we still have the password in memory.
        const handoff = takeSignupHandoff(email);
        if (handoff) {
          try {
            const r = await auth.signIn(handoff.email, handoff.password);
            if (r.status === "SIGNED_IN") { navigate("/", { replace: true }); return; }
          } catch { /* fall through to /login */ }
        }
        navigate("/login", { replace: true, state: { email, flash: "Conta confirmada! Entre para continuar." } });
      } catch (err) {
        console.error("Confirmation failed:", err);
        track(EVENTS.CONFIRM_FAILED, { code: err?.code || "unknown" });
        if (err?.code === "NotAuthorizedException" && /CONFIRMED/i.test(err?.message || "")) {
          clearPendingConfirmation();
          navigate("/login", { replace: true, state: { email, flash: "Sua conta já estava confirmada. Entre para continuar." } });
          return;
        }
        setSubmitError(translateAuthError(err));
      }
    },
  });

  const handleResend = async () => {
    if (!email || cooldown > 0) return;
    setResending(true);
    setSubmitError(null);
    try {
      await auth.resendConfirmationCode(email);
      track(EVENTS.CONFIRM_RESEND);
      setInfo(`Novo código enviado para ${email}.`);
      setCooldown(RESEND_COOLDOWN_S);
      setEditingEmail(false);
    } catch (err) {
      console.error("Resend failed:", err);
      setSubmitError(translateAuthError(err));
    } finally {
      setResending(false);
    }
  };

  const handleChangeEmail = (e) => {
    e.preventDefault();
    const next = normalizeEmail(new FormData(e.currentTarget).get("email"));
    if (!/^\S+@\S+\.\S+$/.test(next)) { setSubmitError("Informe um e-mail válido."); return; }
    setEmail(next);
    setPendingConfirmation(next);
    setEditingEmail(false);
    setSubmitError(null);
    setInfo(null);
  };

  const handleStartOver = () => {
    clearPendingConfirmation();
    clearSignupHandoff();
    navigate("/cadastro", { replace: true });
  };

  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to="/" replace />;

  return (
    <AuthLayout
      title="Confirme seu e-mail"
      subtitle="Digite o código que enviamos para você."
      footer={
        <p className="mb-0">
          Já confirmou?{" "}
          <Link to="/login" state={{ email }} className="fw-medium text-primary">Entrar</Link>
        </p>
      }
    >
      {resumed && (
        <Alert color="info" className="small">
          Você já começou um cadastro com este e-mail. Se ainda tem o código, é só digitar abaixo. Se não, peça um novo.
        </Alert>
      )}
      {info && <Alert color="success">{info}</Alert>}
      {submitError && <Alert color="danger">{submitError}</Alert>}

      {editingEmail ? (
        <Form onSubmit={handleChangeEmail} noValidate className="mb-3">
          <Label htmlFor="ca-email">E-mail cadastrado</Label>
          <div className="input-group">
            <Input id="ca-email" name="email" type="email" inputMode="email" autoComplete="email" defaultValue={email} autoFocus />
            <Button color="secondary" type="submit">OK</Button>
          </div>
        </Form>
      ) : (
        <p className="text-muted mb-3">
          Código enviado para <strong>{email}</strong>.{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setEditingEmail(true)}>
            E-mail errado?
          </button>
        </p>
      )}

      <Form onSubmit={form.handleSubmit} noValidate>
        <div className="mb-3">
          <Label htmlFor="ca-code">Código de confirmação</Label>
          <Input
            id="ca-code" name="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
            autoComplete="one-time-code" placeholder="000000" autoFocus={!!email}
            className="form-control-lg text-center fw-semibold" style={{ letterSpacing: "0.4em" }}
            value={form.values.code}
            onChange={(e) => form.setFieldValue("code", e.target.value.replace(/\D/g, "").slice(0, 6))}
            onBlur={form.handleBlur}
            invalid={!!(form.touched.code && form.errors.code)}
            disabled={!email}
          />
          <FormFeedback>{form.errors.code}</FormFeedback>
        </div>

        <div className="d-grid mb-3">
          <Button color="primary" type="submit" disabled={form.isSubmitting || !email}>
            {form.isSubmitting && <Spinner size="sm" className="me-2" />}
            Confirmar
          </Button>
        </div>

        <div className="text-center small">
          <span className="text-muted">Não recebeu? </span>
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={handleResend}
            disabled={!email || cooldown > 0 || resending}>
            {resending ? "Enviando..." : cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
          </button>
          <span className="text-muted"> · </span>
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline text-muted" onClick={handleStartOver}>
            Começar de novo
          </button>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default ConfirmAccount;
